import { useState } from 'react';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import {
  ArrowRightToLine,
  Copy,
  CornerDownRight,
  CornerUpLeft,
  GripVertical,
  MoreVertical,
  Pencil,
  Play,
  ListTree,
  RotateCcw,
  Settings2,
  Trash2,
  Unlink,
} from 'lucide-react';
import {
  canBindList,
  TAXONOMY_FIELD_TYPE_LABELS,
  taxonomyLevelLabel,
  type TaxonomyField,
} from '@/entities/taxonomy';
import type { ResolvedList } from '@/entities/taxonomy-list';
import { type SchemaProblem } from '../../lib/validateSchema';
import { getStandardFieldByKey } from '../FieldLibraryModal/standard-fields';
import { patternToString } from '../../lib/trackingPattern';
import { describeLink } from '../../lib/linkedSchema';
import { ProblemTriangle } from '../../../components/ProblemTriangle/ProblemTriangle';
import { describeProblems, violationSeverityOf } from './fieldsEditorShared';
import { useReportedEditorOpen } from './useReportedEditorOpen';

interface FieldHeaderCellProps {
  field: TaxonomyField;
  allFields: TaxonomyField[];
  /** For a linked field: the target schema's display name. */
  targetName?: string;
  readOnly: boolean;
  isSubfield: boolean;
  dragHandleProps?: Record<string, unknown>;
  onRename: (name: string) => void;
  onRenameEditingChange: (open: boolean) => void;
  onEdit: () => void;
  onConfigure: () => void;
  onReset: () => void;
  onLinkList: () => void;
  onUnlinkList: () => void;
  /** Set when this column is bound to a shared list. */
  boundList?: ResolvedList;
  /** Validate findings anchored to this whole column. */
  problems?: SchemaProblem[];
  onGenerateSample: () => void;
  onAddRight: () => void;
  onAddSubfield: () => void;
  onDetach: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

/**
 * Column header: field name (required shown with a trailing asterisk) + a quiet
 * type subtitle. On hover a ⋮ menu (Trackers pattern) exposes Edit / Add field
 * to the right / Duplicate / Delete; Edit opens the field modal to change
 * title, type and required — keeping the header itself low-noise.
 */
export function FieldHeaderCell({
  field,
  allFields,
  targetName,
  readOnly,
  isSubfield,
  dragHandleProps,
  onRename,
  onRenameEditingChange,
  onEdit,
  onConfigure,
  onReset,
  onLinkList,
  onUnlinkList,
  boundList,
  problems,
  onGenerateSample,
  onAddRight,
  onAddSubfield,
  onDetach,
  onDuplicate,
  onRemove,
}: FieldHeaderCellProps) {
  const { t } = useTranslation('knowledge');
  const [editing, setEditingState] = useState(false);
  const reportRename = useReportedEditorOpen(onRenameEditingChange);
  const setEditing = (open: boolean) => {
    setEditingState(open);
    reportRename(open);
  };
  const [draft, setDraft] = useState(field.name);
  const problemSummary = describeProblems(problems);
  const problemSeverity = violationSeverityOf(null, problems);
  const isComputed = field.type === 'computed';
  const isLinked = field.type === 'linked';
  const linkedSchemaFieldsOn = useBooleanFlagValue('taxonomy_linked_schema_fields', false);
  // `all` means "every level", which is the default and not worth a badge. Null
  // for an unrecognised stored value, which the badge then omits rather than
  // rendering the word "null" as it used to.
  const levelLabel = field.level && field.level !== 'all' ? taxonomyLevelLabel(field.level) : null;
  const isGenerated = isComputed || isLinked;
  // Library-sourced fields can be reset to their catalog defaults.
  const canReset = !isGenerated && !!getStandardFieldByKey(field.libraryKey);
  // Generated columns show what they resolve to: a Variable its pattern, a
  // linked field its target + source (or a prompt to configure).
  const subtitle = isComputed
    ? field.pattern?.length
      ? patternToString(field.pattern, allFields)
      : t('taxonomy.fieldsEditor.variableNotConfigured')
    : isLinked
      ? describeLink(field.link, targetName)
      : boundList
        ? // WHICH list drives this column, WHICH version, and whether that
          // version can still change. A following column showed the bare name,
          // so the two states differed only by the presence of `· v3` — which
          // reads as decoration rather than as "pinned", and told nobody which
          // vocabulary the generator would actually use.
          // No version yet means the list is still loading or could not be
          // resolved at all — `listName` already says which. Appending
          // "following v?" there would put a version claim on every cold load of
          // every bound column, which is the common path.
          boundList.versionNumber === null
          ? boundList.listName
          : boundList.follows
            ? i18n.t('knowledge:taxonomy.fieldsEditor.listFollowingVersion', {
                name: boundList.listName,
                version: boundList.versionNumber,
              })
            : i18n.t('knowledge:taxonomy.fieldsEditor.listPinnedToVersion', {
                name: boundList.listName,
                version: boundList.versionNumber,
              })
        : isSubfield
          ? i18n.t('knowledge:taxonomy.fieldsEditor.subfieldOfType', {
              type: TAXONOMY_FIELD_TYPE_LABELS[field.type],
            })
          : TAXONOMY_FIELD_TYPE_LABELS[field.type];
  const emitsDisplayAsCode =
    !isGenerated && !!boundList && boundList.versionNumber !== null && !field.listRef?.codeColumnId;
  const headerSubtitle = emitsDisplayAsCode
    ? i18n.t('knowledge:taxonomy.fieldsEditor.withCodesSameAsDisplay', { label: subtitle })
    : subtitle;

  const startEdit = () => {
    if (readOnly) return;
    setDraft(field.name);
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== field.name) onRename(next);
  };

  return (
    <div className="flex items-start justify-between gap-1">
      <div className="flex min-w-0 items-start gap-1">
        {dragHandleProps && (
          <button
            type="button"
            aria-label={t('taxonomy.fieldsEditor.dragReorderColumn')}
            {...(dragHandleProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            className="mt-0.5 shrink-0 cursor-grab touch-none text-muted-foreground opacity-0 transition-opacity group-hover/field:opacity-100 active:cursor-grabbing"
          >
            <GripVertical className="size-3.5" />
          </button>
        )}
        {isSubfield && (
          <CornerDownRight
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
            aria-label={t('taxonomy.fieldsEditor.subfield')}
          />
        )}
        <div className="flex min-w-0 flex-col">
          {editing ? (
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the input only mounts once the user starts editing, so focusing it is the interaction
              autoFocus
              value={draft}
              maxLength={100}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setDraft(field.name);
                  setEditing(false);
                }
              }}
              className="w-full rounded border border-primary bg-background px-1 py-0.5 text-sm font-medium text-foreground outline-none"
            />
          ) : (
            <span className="flex min-w-0 items-center gap-1">
              {/* Read-only degrades to text rather than a dead button: a column
                  name that looks pressable and never is reads as broken. */}
              {readOnly ? (
                <span className="truncate text-left text-sm font-medium text-foreground">
                  {field.name}
                  {field.required && <span className="ml-0.5 text-destructive">*</span>}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={startEdit}
                  title={t('taxonomy.fieldsEditor.clickRename')}
                  className="truncate text-left text-sm font-medium text-foreground decoration-dotted underline-offset-2 hover:underline"
                >
                  {field.name}
                  {field.required && <span className="ml-0.5 text-destructive">*</span>}
                </button>
              )}
              {/* Column-level Validate findings — a broken binding, a rule that
                  can never fire, a pattern nothing enforces. Same triangle the
                  cells use, so one icon means one thing across the grid. */}
              {problemSummary && (
                <ProblemTriangle message={problemSummary} warning={problemSeverity === 'warning'} />
              )}
            </span>
          )}
          <span className="flex min-w-0 items-center gap-1">
            {boundList && (
              <ListTree
                className={`size-3 shrink-0 ${boundList.error ? 'text-destructive' : 'text-muted-foreground'}`}
                aria-label={boundList.error ?? t('taxonomy.fieldsEditor.boundSharedList')}
              />
            )}
            {levelLabel && (
              <span
                className="shrink-0 rounded bg-accent px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                title={t('taxonomy.fieldsEditor.hierarchyLevel', { level: levelLabel })}
              >
                {levelLabel}
              </span>
            )}
            <span
              className={`truncate text-xs font-normal text-muted-foreground ${isGenerated ? 'font-mono' : ''}`}
              title={isGenerated ? subtitle : undefined}
            >
              {headerSubtitle}
            </span>
          </span>
        </div>
      </div>
      {!readOnly && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="mini"
              aria-label={t('taxonomy.fieldsEditor.fieldActions', { name: field.name })}
              className="size-6 shrink-0 p-0 text-muted-foreground opacity-0 transition-opacity group-hover/field:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreVertical className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isGenerated ? (
              <>
                <DropdownMenuItem onSelect={onConfigure}>
                  <Settings2 className="mr-2 size-4" />
                  {t('taxonomy.fieldsEditor.configure')}
                </DropdownMenuItem>
                {isComputed && (
                  <DropdownMenuItem onSelect={onGenerateSample} disabled={!field.pattern?.length}>
                    <Play className="mr-2 size-4" />
                    {t('taxonomy.fieldsEditor.generateSample')}
                  </DropdownMenuItem>
                )}
              </>
            ) : (
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.edit')}
              </DropdownMenuItem>
            )}
            {/* Shared-list binding — only meaningful for option-bearing columns,
                so LINKING is offered on those alone.

                UNLINKING is offered wherever a binding exists, including on a
                generated column: every gate here is a gate on the only way out
                of a binding that should never have been written, and a stored
                row can carry one whatever its type says. */}
            {boundList ? (
              <>
                {!isGenerated && canBindList(field.type) && (
                  <DropdownMenuItem onSelect={onLinkList}>
                    <ListTree className="mr-2 size-4" />
                    {t('taxonomy.fieldsEditor.editListBinding')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={onUnlinkList}>
                  <Unlink className="mr-2 size-4" />
                  {t('taxonomy.fieldsEditor.unlinkList')}
                </DropdownMenuItem>
              </>
            ) : (
              !isGenerated &&
              canBindList(field.type) && (
                <DropdownMenuItem onSelect={onLinkList}>
                  <ListTree className="mr-2 size-4" />
                  {t('taxonomy.fieldsEditor.useSharedList')}
                </DropdownMenuItem>
              )
            )}
            {canReset && !boundList && (
              <DropdownMenuItem onSelect={onReset}>
                <RotateCcw className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.resetDefault')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={onAddRight}>
              <ArrowRightToLine className="mr-2 size-4" />
              {t('taxonomy.fieldsEditor.addFieldRight')}
            </DropdownMenuItem>
            {!isSubfield && !isGenerated && (
              <DropdownMenuItem onSelect={onAddSubfield}>
                <CornerDownRight className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.addSubfield')}
              </DropdownMenuItem>
            )}
            {/* The way back out of a cluster. Without it a subfield was stuck:
                its section is locked to the parent, so the only escape was
                deleting and recreating it. */}
            {isSubfield && (
              <DropdownMenuItem onSelect={onDetach}>
                <CornerUpLeft className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.detachParent')}
              </DropdownMenuItem>
            )}
            {(!isLinked || linkedSchemaFieldsOn) && (
              <DropdownMenuItem onSelect={onDuplicate}>
                <Copy className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.duplicate')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={onRemove}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {t('taxonomy.fieldsEditor.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
